import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../App';
import { toast } from 'sonner';
import { ArrowRight, Star, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SMILEY_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_121ad1ad-d724-4658-ab95-7dc4ce7b4a83/artifacts/766c9fb51cb57f3d_image.png";
const HALLOWEEN_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_121ad1ad-d724-4658-ab95-7dc4ce7b4a83/artifacts/b8d198d71e64f938_unnamed.png";
const HERO_IMG = "https://images.unsplash.com/photo-1542406775-ade58c52d2e4?w=1200&q=80";

const Home = () => {
  useEffect(() => { document.title = 'Crookid Smilez | Urban. Raw. Defined.'; }, []);
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedSizes, setSelectedSizes] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, testimonialsRes] = await Promise.all([
          fetch(`${API}/products`),
          fetch(`${API}/testimonials`)
        ]);
        const productsData = await productsRes.json();
        const testimonialsData = await testimonialsRes.json();
        setProducts(productsData.slice(0, 6));
        setTestimonials(testimonialsData);
      } catch (error) {
        // Error fetching data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success('Welcome to the squad.');
        setEmail('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Already recruited');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const handleQuickAdd = useCallback((product) => {
    const size = selectedSizes[product.id] || product.sizes[0];
    addToCart({
      product_id: product.id,
      name: product.name,
      price: product.price,
      size,
      color: product.colors[0],
      quantity: 1,
      image: product.images[0]
    });
    toast.success(`${product.name} added`);
  }, [selectedSizes, addToCart]);

  const categoryPills = [
    { label: 'HOODIES', path: '/shop/hoodies' },
    { label: 'TEES', path: '/shop/tshirts' },
    { label: 'OUTERWEAR', path: '/shop/jackets' },
    { label: 'ALL DROPS', path: '/shop' },
  ];

  return (
    <div className="fade-in" data-testid="home-page">
      {/* Split-Screen Hero */}
      <section className="flex flex-col lg:flex-row min-h-[80vh]" data-testid="hero-section">
        {/* Left Panel */}
        <div className="w-full lg:w-1/2 p-6 sm:p-12 lg:p-16 flex flex-col justify-between bg-cs-dark border-b lg:border-b-0 lg:border-r border-cs-border relative overflow-hidden">
          {/* Noise texture overlay */}
          <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(75, 83, 32, 0.15) 0%, rgba(13, 13, 14, 0.95) 100%)'}} />

          <div className="relative z-10">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cs-muted mb-8">
              Collection 04 // Distortion Warehouse
            </p>

            <h1 className="font-graffiti text-5xl sm:text-6xl lg:text-7xl uppercase text-cs-text tracking-wider leading-none mb-4" style={{textShadow: '0 2px 10px rgba(0,0,0,0.8)'}}>
              Crookid<br />Smilez
            </h1>

            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-cs-ochre/70 border-t border-dashed border-cs-ochre" />
              <p className="font-mono uppercase tracking-[0.3em] text-xs sm:text-sm text-cs-ochre font-semibold">
                Urban. Raw. Defined.
              </p>
              <div className="h-px flex-1 max-w-[60px] bg-cs-ochre/70 border-t border-dashed border-cs-ochre" />
            </div>

            <p className="font-mono text-sm text-cs-muted leading-relaxed max-w-md mb-10">
              Streetwear forged in the underground. Built for individuals who use their differences as strength. From newborns to OG's.
            </p>
          </div>

          <div className="relative z-10 space-y-6">
            {/* Category Pills */}
            <div className="flex flex-wrap gap-2">
              {categoryPills.map(pill => (
                <Link
                  key={pill.path}
                  to={pill.path}
                  className="border border-cs-border text-cs-muted font-mono text-xs uppercase tracking-wider px-4 py-2 hover:border-cs-ochre hover:text-cs-ochre transition-colors"
                  data-testid={`category-pill-${pill.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {pill.label}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/shop"
                className="bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-8 py-4 hover:bg-cs-olive2 transition-colors text-center flex items-center justify-center gap-2"
                data-testid="hero-explore-drops-button"
              >
                Shop The Collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/about"
                className="border border-cs-border text-cs-muted font-mono uppercase tracking-wider text-sm px-8 py-4 hover:border-cs-ochre hover:text-cs-ochre transition-colors text-center"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>

        {/* Right Panel - Emblem */}
        <div className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-[640px] bg-cs-dark overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(75, 83, 32, 0.2) 0%, rgba(13, 13, 14, 0.95) 100%)'}} />
          <img
            src={SMILEY_LOGO}
            alt="Crookid Smilez Verified Emblem"
            className="relative z-10 w-[70%] max-w-[480px] drop-shadow-2xl"
          />
          {/* Floating badges */}
          <div className="absolute bottom-6 left-6 right-6 z-10">
            <div className="inline-block bg-cs-bg/80 backdrop-blur-sm border border-cs-border px-4 py-2">
              <p className="font-mono text-xs text-cs-ochre uppercase tracking-wider">Authentic // Original // Streetwear</p>
            </div>
          </div>
          <div className="absolute top-6 right-6 z-10">
            <div className="bg-cs-bg/60 backdrop-blur-sm border border-cs-border px-3 py-1.5">
              <p className="font-mono text-[10px] text-cs-muted uppercase tracking-widest">Est. 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Halloween Banner */}
      <section className="relative py-0 overflow-hidden" data-testid="halloween-section">
        <div className="relative">
          <img src={HALLOWEEN_IMG} alt="Crookid Smilez Halloween" className="w-full h-[40vh] md:h-[50vh] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-cs-bg via-cs-bg/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="max-w-7xl mx-auto">
              <span className="inline-block bg-[#FF6600] text-cs-dark font-mono font-bold uppercase text-xs px-3 py-1.5 tracking-wider mb-3">
                Limited Edition
              </span>
              <h2 className="font-anton text-3xl md:text-5xl uppercase text-cs-text mb-3" style={{textShadow: '2px 2px 8px rgba(0,0,0,0.7)'}}>
                Halloween Collection
              </h2>
              <Link to="/shop/hoodies" className="inline-flex items-center gap-2 bg-[#FF6600] text-cs-dark font-mono uppercase tracking-wider text-sm px-6 py-3 hover:bg-[#FF6600]/80 transition-colors" data-testid="halloween-shop-btn">
                Shop Halloween <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fresh Drops - Product Grid */}
      <section className="py-16 sm:py-20 bg-cs-concrete" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-2">Collection</p>
              <h2 className="font-anton text-3xl sm:text-4xl uppercase text-cs-text tracking-tight">Fresh Drops</h2>
            </div>
            <Link to="/shop" className="font-mono text-xs uppercase tracking-widest text-cs-muted hover:text-cs-ochre transition-colors flex items-center gap-2" data-testid="view-all-link">
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-cs-card border border-cs-border h-[480px] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {products.map((product) => (
                <div key={product.id} className="bg-cs-card border border-cs-border hover:border-cs-olive2 transition-all duration-300 group flex flex-col" data-testid={`product-card-${product.id}`}>
                  {/* Image */}
                  <Link to={`/product/${product.id}`} className="relative aspect-[3/4] overflow-hidden bg-cs-dark">
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 bg-cs-olive text-cs-text font-mono text-xs px-2.5 py-1 tracking-wider uppercase">
                      {product.category}
                    </span>
                    {product.shopify_url && (
                      <span className="absolute top-3 right-3 bg-cs-ochre text-cs-dark font-mono text-xs px-2 py-0.5 tracking-wider uppercase">
                        Shopify
                      </span>
                    )}
                  </Link>

                  {/* Info */}
                  <div className="p-4 border-t border-dashed border-cs-ochre/30 flex-1 flex flex-col">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-bold text-cs-text text-sm uppercase tracking-wide mb-1">{product.name}</h3>
                    </Link>
                    <p className="font-mono text-cs-ochre text-lg mb-3">${product.price.toFixed(2)}</p>

                    {/* Size Chips */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {product.sizes.slice(0, 5).map(size => (
                        <button
                          key={`size-${size}-${product.id}`}
                          onClick={() => setSelectedSizes(prev => ({ ...prev, [product.id]: size }))}
                          className={`font-mono text-xs px-2.5 py-1 border transition-colors ${
                            (selectedSizes[product.id] || product.sizes[0]) === size
                              ? 'bg-cs-ochre text-cs-dark border-cs-ochre'
                              : 'border-cs-border text-cs-muted hover:border-cs-olive2'
                          }`}
                          data-testid={`size-chip-${size}-${product.id}`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>

                    {/* Quick Add */}
                    <button
                      onClick={() => handleQuickAdd(product)}
                      className="mt-auto w-full bg-cs-dark border border-cs-border text-cs-text font-mono uppercase tracking-wider text-xs py-2.5 hover:bg-cs-olive hover:border-cs-olive transition-colors flex items-center justify-center gap-2"
                      data-testid={`quick-add-button-${product.id}`}
                    >
                      <ShoppingBag className="w-3.5 h-3.5" /> Quick Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 sm:py-20 bg-cs-olive/10 border-y border-cs-border" data-testid="newsletter-section">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={SMILEY_LOGO} alt="Crookid Smilez" className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h2 className="font-anton text-3xl sm:text-4xl uppercase mb-3 text-cs-text tracking-tight">
            Join The Squad
          </h2>
          <p className="font-mono text-xs text-cs-muted uppercase tracking-wider mb-8">
            Enter email // Gain early access to drops + exclusives
          </p>
          <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="bg-cs-bg border border-cs-border text-cs-text font-mono text-sm p-3 focus:outline-none focus:border-cs-ochre transition-colors flex-1 placeholder:text-cs-muted/50"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              className="bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-6 py-3 hover:bg-cs-olive2 transition-colors"
              data-testid="newsletter-submit-button"
            >
              Recruit
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
